import React, { useEffect, useMemo, useState } from "react";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  getDocs,
  getFirestore,
  orderBy,
  query,
  Timestamp,
  where,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { app } from "../firebase";
import { resolveUserRole } from "../utils/authRoles";
import "./CustomerPortal.css";

function CustomerPortal() {
  const auth = getAuth(app);
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const [user, setUser] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [orders, setOrders] = useState([]);
  const [pricesList, setPricesList] = useState([]);
  const [searchDelivery, setSearchDelivery] = useState("");

  const [formData, setFormData] = useState({
    fromLocation: "",
    fromContact: "",
    fromContactPhone: "",
    toLocation: "",
    toPrice: 0,
    toDetails: "",
    toName: "",
    toContact: "",
    deliveryInstructions: "",
  });

  const filteredPrices = useMemo(() => {
    if (!searchDelivery.trim()) return pricesList;
    const lower = searchDelivery.trim().toLowerCase();
    return pricesList.filter(
      (item) =>
        item.location?.toLowerCase().includes(lower) ||
        item.route?.toLowerCase().includes(lower)
    );
  }, [pricesList, searchDelivery]);

  const resetForm = () => {
    setFormData({
      fromLocation: "",
      fromContact: "",
      fromContactPhone: "",
      toLocation: "",
      toPrice: 0,
      toDetails: "",
      toName: "",
      toContact: "",
      deliveryInstructions: "",
    });
    setSearchDelivery("");
  };

  const fetchOrders = async (clientId) => {
    if (!clientId) return;
    setLoadingOrders(true);
    try {
      const orderQuery = query(
        collection(db, "Orders"),
        where("clientId", "==", clientId)
      );
      const snap = await getDocs(orderQuery);
      const data = snap.docs
        .map((orderDoc) => ({ id: orderDoc.id, ...orderDoc.data() }))
        .sort((a, b) => {
          const aTime = a.timestamp?.toMillis?.() || 0;
          const bTime = b.timestamp?.toMillis?.() || 0;
          return bTime - aTime;
        });
      setOrders(data);
    } catch (error) {
      console.error("Error fetching customer orders:", error);
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (activeUser) => {
      if (!activeUser) {
        navigate("/login");
        return;
      }

      setUser(activeUser);
      try {
        const { role, profile } = await resolveUserRole(db, activeUser.uid);
        if (role !== "client" || !profile) {
          navigate("/login");
          return;
        }

        setClientProfile(profile);
        await fetchOrders(profile.id);
      } catch (error) {
        console.error("Error loading profile:", error);
      } finally {
        setLoadingPage(false);
      }
    });

    return () => unsub();
  }, [auth, db, navigate]);

  useEffect(() => {
    const loadPrices = async () => {
      try {
        const priceQuery = query(collection(db, "prices"), orderBy("location", "asc"));
        const snap = await getDocs(priceQuery);
        setPricesList(snap.docs.map((priceDoc) => ({ id: priceDoc.id, ...priceDoc.data() })));
      } catch (error) {
        console.error("Error fetching prices:", error);
      }
    };

    loadPrices();
  }, [db]);

  const handleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));
  };

  const handleSelectDelivery = (item) => {
    setFormData((prev) => ({
      ...prev,
      toLocation: item.location || "",
      toPrice: Number(item.price || 0),
    }));
    setSearchDelivery(item.location || "");
  };

  const validateForm = () => {
    const required = [
      "fromLocation",
      "fromContact",
      "fromContactPhone",
      "toLocation",
      "toName",
      "toContact",
    ];

    for (const field of required) {
      if (!formData[field]?.toString().trim()) {
        alert("Please fill all required order fields.");
        return false;
      }
    }

    const phoneFields = [formData.fromContactPhone, formData.toContact];
    for (const phone of phoneFields) {
      if (!/^\d+$/.test(phone.toString().trim())) {
        alert("Phone numbers should contain digits only.");
        return false;
      }
    }

    return true;
  };

  const handlePlaceOrder = async (event) => {
    event.preventDefault();
    if (!clientProfile || !user) return;
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      await addDoc(collection(db, "Orders"), {
        orderType: "Existing Client",
        clientId: clientProfile.id,
        clientUid: user.uid,
        clientName: clientProfile.full_name || clientProfile.businessName || "",
        clientPhone: clientProfile.userphone || "",
        fromLocation: formData.fromLocation.trim(),
        fromContact: formData.fromContact.trim(),
        fromContactPhone: formData.fromContactPhone.trim(),
        toLocation: formData.toLocation.trim(),
        toPrice: Number(formData.toPrice || 0),
        toDetails: formData.toDetails.trim(),
        toName: formData.toName.trim(),
        toContact: formData.toContact.trim(),
        deliveryInstructions: formData.deliveryInstructions.trim(),
        amount: Number(formData.toPrice || 0),
        status: "Pending",
        timestamp: Timestamp.now(),
        createdByUid: user.uid,
        createdByRole: "client",
      });

      setShowModal(false);
      resetForm();
      await fetchOrders(clientProfile.id);
    } catch (error) {
      console.error("Error creating order:", error);
      alert(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  if (loadingPage) {
    return (
      <div className="cp-loader">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div className="customer-portal">
      <header className="cp-header">
        <div>
          <p className="cp-eyebrow">Customer Portal</p>
          <h1>{clientProfile?.businessName || clientProfile?.full_name || "My Orders"}</h1>
          <p className="cp-subtitle">
            Create delivery requests and track all your order statuses in one place.
          </p>
        </div>
        <div className="cp-actions">
          <Button
            variant="warning"
            className="cp-primary-btn"
            onClick={() => setShowModal(true)}
          >
            Place Order
          </Button>
          <Button variant="outline-secondary" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </header>

      <section className="cp-card">
        <div className="cp-card-top">
          <h5>My Orders</h5>
          <span>{orders.length} total</span>
        </div>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Date</th>
              <th>Status</th>
              <th>Pickup</th>
              <th>Delivery</th>
              <th>Recipient</th>
              <th>Amount (KES)</th>
            </tr>
          </thead>
          <tbody>
            {loadingOrders ? (
              <tr>
                <td colSpan="6" className="text-center">
                  Loading orders...
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  No orders yet. Use Place Order to create one.
                </td>
              </tr>
            ) : (
              orders.map((order) => (
                <tr key={order.id}>
                  <td>{order.timestamp?.toDate?.().toLocaleString() || "-"}</td>
                  <td>{order.status || "-"}</td>
                  <td>{order.fromLocation || "-"}</td>
                  <td>{order.toLocation || "-"}</td>
                  <td>{order.toName || "-"}</td>
                  <td>{order.amount || 0}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </section>

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Place a New Order</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handlePlaceOrder}>
            <h6>Client</h6>
            <Form.Group className="mb-3">
              <Form.Label>Client Name</Form.Label>
              <Form.Control
                value={clientProfile?.full_name || clientProfile?.businessName || ""}
                disabled
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Client Phone</Form.Label>
              <Form.Control value={clientProfile?.userphone || ""} disabled />
            </Form.Group>

            <h6>Pickup</h6>
            <Form.Group className="mb-3">
              <Form.Label>Pickup Location</Form.Label>
              <Form.Control
                type="text"
                name="fromLocation"
                value={formData.fromLocation}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact Person</Form.Label>
              <Form.Control
                type="text"
                name="fromContact"
                value={formData.fromContact}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Contact Person Phone</Form.Label>
              <Form.Control
                type="text"
                name="fromContactPhone"
                value={formData.fromContactPhone}
                onChange={handleChange}
              />
            </Form.Group>

            <h6>Delivery</h6>
            <Form.Group className="mb-3">
              <Form.Label>Delivery Location</Form.Label>
              <Form.Control
                type="text"
                value={searchDelivery}
                onChange={(event) => setSearchDelivery(event.target.value)}
                placeholder="Search delivery location"
              />
              {searchDelivery && (
                <div className="autocomplete-dropdown alert alert-info dropDCustom">
                  {filteredPrices.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => handleSelectDelivery(item)}
                      className="dropDCustomitem"
                    >
                      {item.location} ({item.route}) - KES {item.price}
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Delivery Details (Floor, Door, etc)</Form.Label>
              <Form.Control
                type="text"
                name="toDetails"
                value={formData.toDetails}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Recipient Name</Form.Label>
              <Form.Control
                type="text"
                name="toName"
                value={formData.toName}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Recipient Contact</Form.Label>
              <Form.Control
                type="text"
                name="toContact"
                value={formData.toContact}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Delivery Instructions</Form.Label>
              <Form.Control
                type="text"
                name="deliveryInstructions"
                value={formData.deliveryInstructions}
                onChange={handleChange}
              />
            </Form.Group>

            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? "Placing..." : "Place Order"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default CustomerPortal;
